import React, { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  MinusCircleOutline,
  PlusCircleOutline,
} from "@olist/design-system-icons";

import {
  AccordionSubHeaderWrapper,
  Icon,
  ListItem,
  StyledNav,
  Title,
} from "./styles";
import { CategoriesTree } from "types/articles";

const ICON_SIZE = 16;

interface AccordionSubHeaderProps {
  items: CategoriesTree[];
  openByPath?: string;
  onClickTrack?: (
    text: string,
    type: "article" | "category" | "sub-category"
  ) => void;
}

export function AccordionSubHeader({
  items,
  onClickTrack,
  openByPath,
}: AccordionSubHeaderProps) {
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});
  const itemRefs = useRef<{
    [key: string]: HTMLDivElement | HTMLLIElement | null;
  }>({});

  function handleOpenedItems(
    itemsParam: CategoriesTree[],
    openByPathParam?: string
  ) {
    if (!openByPathParam) return;

    itemsParam.forEach((item: CategoriesTree) => {
      if (!openByPathParam.startsWith(item.path)) return;

      setOpenItems((prev) => ({
        ...prev,
        [item.path]: true,
      }));

      const currentItem = itemRefs.current[item.path];
      if (currentItem) {
        currentItem.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    });
  }

  useEffect(() => {
    handleOpenedItems(items, openByPath);
  }, [openByPath, items]);

  const toggleOpen = (path: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const trackFolder = (title: string) => {
    onClickTrack?.(title, "sub-category");
  };

  const renderItems = (item: CategoriesTree) => {
    const isOpen = openItems[item.path] || false;

    if (item.isFolder) {
      return (
        <div
          key={`inner-accordion-subheader-${item.path}-${item.id}`}
          ref={(el) => {
            itemRefs.current[item.path] = el;
          }}
        >
          <Title
            onClick={() => {
              toggleOpen(item.path);
              trackFolder(item.title);
            }}
          >
            <Icon isOpen={isOpen}>
              {isOpen ? (
                <MinusCircleOutline height={ICON_SIZE} width={ICON_SIZE} />
              ) : (
                <PlusCircleOutline height={ICON_SIZE} width={ICON_SIZE} />
              )}
            </Icon>
            {item.title}
          </Title>
          {isOpen && (
            <AccordionSubHeader items={item.children} openByPath={openByPath} />
          )}
        </div>
      );
    }

    const listStyle = item.depth === 3 ? "none" : "";

    const onClickNav = () => {
      onClickTrack?.(item.title, "article");
      navigate(item.path.split("/").slice(1).join("/"));
    };

    return (
      <ListItem
        key={`list-item-${item.path}-${item.id}`}
        listStyle={listStyle}
        ref={(el) => {
          itemRefs.current[item.path] = el;
        }}
      >
        <StyledNav key={item.path} onClick={onClickNav}>
          {item.title}
        </StyledNav>
      </ListItem>
    );
  };

  return (
    <AccordionSubHeaderWrapper>
      {items.map(renderItems)}
    </AccordionSubHeaderWrapper>
  );
}
